import { applyTabulatorRuntimeI18n } from './tabulatorRuntimeI18n';
import sk from '../locales/sk.json';

/**
 * Quality (QLTY026/028/032) partner type and company-status cell text.
 * Reads current LocaleContext translator; does not mutate row.types or API fields.
 */

const KNOWN_TYPE_KEYS = Object.freeze({
  supplier: 'BusinessPartnerList.PartnerType.Supplier',
  customer: 'BusinessPartnerList.PartnerType.Customer',
  client: 'BusinessPartnerList.PartnerType.Client',
  calibration_company: 'BusinessPartnerList.PartnerType.CalibrationCompany',
  training_company: 'BusinessPartnerList.PartnerType.TrainingCompany',
  service_company: 'BusinessPartnerList.PartnerType.ServiceCompany',
});

export const QUALITY_PARTNER_LIST_COLUMN_I18N_KEYS = Object.freeze({
  name: 'BusinessPartnerList.Column.Name',
  defined_id: 'BusinessPartnerList.Column.DefinedId',
  types: 'BusinessPartnerList.Column.Types',
  address: 'BusinessPartnerList.Column.Address',
  tel_number: 'BusinessPartnerList.Column.TelNumber',
  fax: 'BusinessPartnerList.Column.Fax',
  email: 'BusinessPartnerList.Column.Email',
  web_address: 'BusinessPartnerList.Column.WebAddress',
  contact_person: 'BusinessPartnerList.Column.ContactPerson',
  description: 'BusinessPartnerList.Column.Description',
  partner_status_display: 'QualityPartnerList.Column.PartnerStatus',
  company_status_display: 'QualityPartnerList.Column.PartnerStatus',
  created_at: 'OtkCompanyList.Column.CreatedAt',
  updated_at: 'OtkCompanyList.Column.UpdatedAt',
});

const EMPTY_CELL = '—';

let currentT = (key) => (key && sk[key]) || key || '';

export function setQualityPartnerTranslator(t) {
  currentT = typeof t === 'function' ? t : ((key) => (key && sk[key]) || key || '');
}

function tt(key) {
  if (!key) return '';
  const translated = currentT(key);
  if (translated != null && translated !== '') return translated;
  return sk[key] || key;
}

export function parseCompanyTypes(rowData) {
  const raw = rowData?.types;
  if (Array.isArray(raw)) return raw.map((x) => String(x));
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x));
    } catch (_) {
      /* ignore invalid JSON */
    }
  }
  return [];
}

export function getQualityPartnerTypeTokens(rowData) {
  const tokens = parseCompanyTypes(rowData);
  const seen = new Set(tokens);
  const partyMode = String(rowData?.partner_status_by_type?.party_mode ?? '');
  if ((partyMode === 'customer' || partyMode === 'client') && !seen.has(partyMode)) {
    tokens.push(partyMode);
  }
  return tokens;
}

export function qualityPartnerTypeLabel(token) {
  if (token == null || token === '') return '';
  // GSET007 party_mode sentinel, not a partner type checkbox. May leak into companies.types.
  if (token === 'none') return '';
  const key = KNOWN_TYPE_KEYS[token];
  if (key) return tt(key);
  return String(token);
}

/** Tabulator 5.5.2 Sort.findSorter calls value.match on array `types` → TypeError. */
export function qualityPartnerTypeSorter(a, b, aRow, bRow) {
  const aData = aRow && typeof aRow.getData === 'function' ? aRow.getData() : { types: a };
  const bData = bRow && typeof bRow.getData === 'function' ? bRow.getData() : { types: b };
  const left = formatQualityPartnerTypeText(aData);
  const right = formatQualityPartnerTypeText(bData);
  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });
}

export function formatQualityPartnerTypeText(rowData) {
  try {
    const tokens = getQualityPartnerTypeTokens(rowData);
    if (tokens.length === 0) return EMPTY_CELL;
    const labels = [];
    const seen = new Set();
    for (const token of tokens) {
      const label = qualityPartnerTypeLabel(token);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      labels.push(label);
    }
    return labels.length > 0 ? labels.join('/') : EMPTY_CELL;
  } catch (_) {
    return EMPTY_CELL;
  }
}

export function isCompanyActive(rowData) {
  const validity = rowData?.validity;
  return validity === 1 || validity === '1';
}

export function formatQualityPartnerStatusText(rowData) {
  try {
    if (isCompanyActive(rowData)) return tt('Common.Active');
    return tt('QualityPartnerList.Status.Excluded');
  } catch (_) {
    return EMPTY_CELL;
  }
}

export function qualityPartnerTypeHeaderFilter(headerValue, _rowValue, rowData) {
  const query = String(headerValue ?? '').trim().toLowerCase();
  if (!query) return true;
  const text = formatQualityPartnerTypeText(rowData).toLowerCase();
  const raw = getQualityPartnerTypeTokens(rowData).join(' ').toLowerCase();
  return text.includes(query) || raw.includes(query);
}

export function qualityPartnerStatusHeaderFilter(headerValue, _rowValue, rowData) {
  const query = String(headerValue ?? '').trim().toLowerCase();
  if (!query) return true;
  return formatQualityPartnerStatusText(rowData).toLowerCase().includes(query);
}

function isDomElement(el) {
  return !!(el && typeof el.setAttribute === 'function' && typeof el.querySelector === 'function');
}

/** Update header text without Column.updateDefinition (which addColumn+delete, leaving Sort.sortList stale). */
export function setQualityPartnerColumnTitle(col, title) {
  if (!col || title == null) return;
  const def = typeof col.getDefinition === 'function' ? col.getDefinition() : null;
  if (def && typeof def === 'object') def.title = title;
  const el = typeof col.getElement === 'function' ? col.getElement() : null;
  if (!isDomElement(el)) return;
  const titleEl = el.querySelector('.tabulator-col-title');
  if (!titleEl) return;
  const editor = titleEl.querySelector('input.tabulator-title-editor');
  if (editor) editor.value = title;
  else titleEl.textContent = title;
}

function getExistingCellElement(cell) {
  if (!cell) return null;
  const inner = typeof cell._getSelf === 'function' ? cell._getSelf() : null;
  if (inner && inner.loaded === false) return null;
  const el = (inner && inner.element) || (typeof cell.getElement === 'function' ? cell.getElement() : null);
  return isDomElement(el) ? el : null;
}

function getCellRowData(cell) {
  const row = typeof cell.getRow === 'function' ? cell.getRow() : null;
  return row && typeof row.getData === 'function' ? row.getData() : null;
}

/**
 * Tabulator 5.5.2 CellComponent has no reformat(). Titles update in place, but Typ/Stav
 * stay in the previous language unless already-rendered cell DOM is rewritten.
 */
export function refreshQualityPartnerFormattedCell(cell, field) {
  if (!cell) return;
  const resolvedField = field || (typeof cell.getField === 'function' ? cell.getField() : null);
  if (resolvedField !== 'types' && resolvedField !== 'partner_status_display' && resolvedField !== 'company_status_display') return;
  const rowData = getCellRowData(cell);
  const text = resolvedField === 'types'
    ? formatQualityPartnerTypeText(rowData)
    : formatQualityPartnerStatusText(rowData);
  const el = getExistingCellElement(cell);
  if (!isDomElement(el)) return;
  const formatterCell = el.querySelector('.formatterCell');
  if (formatterCell) formatterCell.textContent = text;
  else el.textContent = text;
}

export function applyQualityPartnerListLocale(table, t) {
  if (typeof t === 'function') setQualityPartnerTranslator(t);
  if (!table) return;
  applyTabulatorRuntimeI18n(table, tt, {
    columnTitleKeys: QUALITY_PARTNER_LIST_COLUMN_I18N_KEYS,
    reformatRows: false,
    onRefreshCells: (tbl) => {
      const columns = typeof tbl.getColumns === 'function' ? tbl.getColumns() : [];
      for (const col of columns) {
        const field = typeof col.getField === 'function' ? col.getField() : null;
        if (field === 'types' || field === 'partner_status_display' || field === 'company_status_display') {
          try {
            const cells = typeof col.getCells === 'function' ? col.getCells() : [];
            for (const cell of cells) {
              refreshQualityPartnerFormattedCell(cell, field);
            }
          } catch (_) { /* keep session */ }
        }
      }
    },
  });
}
